<template>
  <div>
    <div class="page-header">
      <h1>📈 股票持倉</h1>
      <p>美股 / 台股 即時淨值</p>
    </div>

    <!-- 新增持股表單 -->
    <div class="add-form card">
      <h3>➕ 新增持股</h3>
      <div class="form-row">
        <div class="form-group">
          <label>市場</label>
          <select v-model="newStock.market" class="input-field">
            <option value="US">🇺🇸 美股</option>
            <option value="TW">🇹🇼 台股</option>
          </select>
        </div>
        <div class="form-group">
          <label>股票代號</label>
          <input
            v-model="newStock.ticker"
            class="input-field"
            :placeholder="newStock.market === 'US' ? 'e.g. TSLA' : 'e.g. 2330'"
            @keyup.enter="addStock"
          />
        </div>
        <div class="form-group">
          <label>股數</label>
          <input
            v-model.number="newStock.shares"
            type="number"
            min="0"
            step="0.0001"
            class="input-field"
            placeholder="e.g. 100"
            @keyup.enter="addStock"
          />
        </div>
        <div class="form-group btn-group">
          <button class="btn-add" @click="addStock" :disabled="adding">
            {{ adding ? '新增中...' : '新增' }}
          </button>
        </div>
      </div>
      <p v-if="addError" class="error-msg">{{ addError }}</p>
    </div>

    <!-- 匯率顯示 -->
    <div v-if="usdtwd" class="rate-bar">
      💱 USD/TWD：<strong>{{ usdtwd.toFixed(2) }}</strong>
      <span class="rate-sep"> ｜ </span>
      📅 資料日期：<strong>{{ priceDate }}</strong>
      <span class="rate-time">（更新於 {{ priceTime }}）</span>
    </div>

    <!-- 載入中 -->
    <div v-if="loading" class="loading-wrap">
      <div class="loading-spinner"></div>
      <p>載入股價中...</p>
    </div>

    <!-- 持倉列表 -->
    <div v-if="!loading">
      <!-- 台股 -->
      <div v-if="twStocks.length > 0" class="section">
        <h3>🇹🇼 台股</h3>
        <div class="stock-table">
          <div class="stock-header stock-row">
            <span>代號</span>
            <span>股數</span>
            <span>現價 (TWD)</span>
            <span>總值 (TWD)</span>
            <span></span>
          </div>
          <div v-for="s in twStocks" :key="s.id" class="stock-row">
            <span class="ticker">{{ s.ticker }}</span>
            <span>{{ formatShares(s.shares) }}</span>
            <span class="price">
              <span v-if="prices[s.ticker] != null">{{ formatPrice(prices[s.ticker]) }}</span>
              <span v-else class="no-price">—</span>
            </span>
            <span class="value">
              <span v-if="prices[s.ticker] != null">{{ formatTWD(prices[s.ticker] * s.shares) }}</span>
              <span v-else class="no-price">—</span>
            </span>
            <span>
              <button class="btn-delete" @click="deleteStock(s.id)" title="刪除">🗑️</button>
            </span>
          </div>
          <div class="stock-row subtotal">
            <span>小計</span>
            <span></span>
            <span></span>
            <span>{{ formatTWD(twTotal) }}</span>
            <span></span>
          </div>
        </div>
      </div>

      <!-- 美股 -->
      <div v-if="usStocks.length > 0" class="section">
        <h3>🇺🇸 美股</h3>
        <div class="stock-table">
          <div class="stock-header stock-row">
            <span>代號</span>
            <span>股數</span>
            <span>現價 (USD)</span>
            <span>總值 (USD)</span>
            <span>總值 (TWD)</span>
            <span></span>
          </div>
          <div v-for="s in usStocks" :key="s.id" class="stock-row us-row">
            <span class="ticker">{{ s.ticker }}</span>
            <span>{{ formatShares(s.shares) }}</span>
            <span class="price">
              <span v-if="prices[s.ticker] != null">$ {{ formatPrice(prices[s.ticker]) }}</span>
              <span v-else class="no-price">—</span>
            </span>
            <span class="value">
              <span v-if="prices[s.ticker] != null">$ {{ formatUSD(prices[s.ticker] * s.shares) }}</span>
              <span v-else class="no-price">—</span>
            </span>
            <span class="value">
              <span v-if="prices[s.ticker] != null && usdtwd">{{ formatTWD(prices[s.ticker] * s.shares * usdtwd) }}</span>
              <span v-else class="no-price">—</span>
            </span>
            <span>
              <button class="btn-delete" @click="deleteStock(s.id)" title="刪除">🗑️</button>
            </span>
          </div>
          <div class="stock-row subtotal">
            <span>小計</span>
            <span></span>
            <span></span>
            <span>$ {{ formatUSD(usTotalUSD) }}</span>
            <span>{{ formatTWD(usTotalTWD) }}</span>
            <span></span>
          </div>
        </div>
      </div>

      <!-- 總計 -->
      <div v-if="holdings.length > 0" class="total-bar">
        <span>📊 總資產（台幣）</span>
        <span class="total-value">{{ formatTWD(grandTotal) }}</span>
      </div>

      <div v-if="holdings.length === 0 && !loading" class="empty-msg">
        還沒有持股，從上方新增吧 👆
      </div>
    </div>

    <!-- 重新整理按鈕 -->
    <div class="refresh-row">
      <button class="btn-refresh" @click="loadAll" :disabled="loading">
        🔄 重新整理報價
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
axios.defaults.baseURL = 'https://mb.kman.idv.tw:9579/'

export default {
  name: 'StockPortfolio',
  data() {
    return {
      holdings: [],
      prices: {},
      usdtwd: null,
      loading: false,
      adding: false,
      addError: '',
      priceTime: '',
      priceDate: '',
      newStock: {
        ticker: '',
        shares: null,
        market: 'US'
      }
    }
  },
  computed: {
    twStocks() {
      return this.holdings.filter(s => s.market === 'TW')
    },
    usStocks() {
      return this.holdings.filter(s => s.market === 'US')
    },
    twTotal() {
      return this.twStocks.reduce((sum, s) => {
        const p = this.prices[s.ticker]
        return p != null ? sum + p * s.shares : sum
      }, 0)
    },
    usTotalUSD() {
      return this.usStocks.reduce((sum, s) => {
        const p = this.prices[s.ticker]
        return p != null ? sum + p * s.shares : sum
      }, 0)
    },
    usTotalTWD() {
      return this.usdtwd ? this.usTotalUSD * this.usdtwd : 0
    },
    grandTotal() {
      return this.twTotal + this.usTotalTWD
    }
  },
  methods: {
    headers() {
      const token = this.$store.getters.setToken
      return { Authorization: `Bearer ${token}` }
    },
    async loadHoldings() {
      const r = await axios.get('get_stocks', { headers: this.headers() })
      this.holdings = r.data.stocks.map(row => ({
        id: row[0],
        ticker: row[1],
        shares: row[2],
        market: row[3]
      }))
    },
    async loadPrices() {
      const r = await axios.get('stock_prices', { headers: this.headers() })
      this.prices = r.data.prices
      this.usdtwd = r.data.usdtwd
      this.priceDate = r.data.price_date || ''
      const now = new Date()
      this.priceTime = now.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })
    },
    async loadAll() {
      this.loading = true
      try {
        await this.loadHoldings()
        if (this.holdings.length > 0) {
          await this.loadPrices()
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    async addStock() {
      this.addError = ''
      if (!this.newStock.ticker) { this.addError = '請輸入股票代號'; return }
      if (!this.newStock.shares || this.newStock.shares <= 0) { this.addError = '請輸入有效股數'; return }
      this.adding = true
      try {
        await axios.post('add_stock', {
          ticker: this.newStock.ticker.trim().toUpperCase(),
          shares: this.newStock.shares,
          market: this.newStock.market,
          token: this.$store.getters.setToken
        })
        this.newStock.ticker = ''
        this.newStock.shares = null
        await this.loadAll()
      } catch (e) {
        this.addError = e.response?.data?.detail || '新增失敗'
      } finally {
        this.adding = false
      }
    },
    async deleteStock(id) {
      if (!confirm('確定刪除這筆持股？')) return
      try {
        await axios.post('delete_stock', {
          id,
          token: this.$store.getters.setToken
        })
        await this.loadAll()
      } catch (e) {
        alert('刪除失敗：' + (e.response?.data?.detail || e.message))
      }
    },
    formatShares(n) {
      return n % 1 === 0 ? n.toLocaleString() : n.toLocaleString(undefined, { maximumFractionDigits: 4 })
    },
    formatPrice(n) {
      return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatUSD(n) {
      return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatTWD(n) {
      return '$ ' + Math.round(n).toLocaleString()
    }
  },
  mounted() {
    this.loadAll()
  }
}
</script>

<style scoped>
.page-header {
  margin-bottom: 1.5rem;
}
.page-header h1 {
  font-size: 1.8rem;
  margin: 0;
}
.page-header p {
  color: #888;
  margin: 0.3rem 0 0;
}

.card {
  background: var(--card-bg, #1e1e2e);
  border-radius: 12px;
  padding: 1.2rem 1.5rem;
  margin-bottom: 1.2rem;
}

.add-form h3 {
  margin: 0 0 1rem;
  font-size: 1rem;
  color: #aaa;
}

.form-row {
  display: flex;
  gap: 0.8rem;
  flex-wrap: wrap;
  align-items: flex-end;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.form-group label {
  font-size: 0.78rem;
  color: #888;
}

.input-field {
  background: #2a2a3e;
  border: 1px solid #444;
  border-radius: 8px;
  color: #eee;
  padding: 0.5rem 0.8rem;
  font-size: 0.95rem;
  min-width: 120px;
}

.input-field:focus {
  outline: none;
  border-color: #7c8fff;
}

.btn-group {
  padding-bottom: 0;
}

.btn-add {
  background: #5c7cfa;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1.4rem;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-add:hover:not(:disabled) {
  background: #4c6ef5;
}

.btn-add:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error-msg {
  color: #f06060;
  font-size: 0.85rem;
  margin: 0.5rem 0 0;
}

.rate-bar {
  background: #1a2a1a;
  border-left: 3px solid #40c057;
  border-radius: 8px;
  padding: 0.6rem 1rem;
  margin-bottom: 1.2rem;
  font-size: 0.9rem;
  color: #aaa;
}

.rate-bar strong {
  color: #40c057;
  font-size: 1rem;
}

.rate-time {
  font-size: 0.8rem;
  color: #666;
}

.loading-wrap {
  text-align: center;
  padding: 2rem;
  color: #aaa;
}

.loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #333;
  border-top-color: #5c7cfa;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 0.8rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.section {
  margin-bottom: 1.5rem;
}

.section h3 {
  font-size: 1rem;
  color: #aaa;
  margin: 0 0 0.7rem;
}

.stock-table {
  background: var(--card-bg, #1e1e2e);
  border-radius: 12px;
  overflow: hidden;
}

.stock-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 40px;
  padding: 0.65rem 1rem;
  gap: 0.5rem;
  align-items: center;
  border-bottom: 1px solid #2a2a3a;
  font-size: 0.92rem;
}

.us-row {
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr 40px;
}

.stock-header {
  grid-template-columns: 1fr 1fr 1fr 1fr 40px;
  color: #666;
  font-size: 0.8rem;
  background: #161622;
}

.stock-header.us-row {
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr 40px;
}

.ticker {
  font-weight: bold;
  color: #7c8fff;
}

.price {
  color: #e8d44d;
}

.value {
  color: #51cf66;
}

.no-price {
  color: #555;
}

.stock-row span:not(.ticker):not(.price):not(.value):not(.no-price) {
  color: #ccc;
}

.subtotal {
  background: #161622;
  font-weight: bold;
  color: #aaa;
  border-bottom: none;
}

.btn-delete {
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  opacity: 0.5;
  transition: opacity 0.2s;
  padding: 0;
}

.btn-delete:hover {
  opacity: 1;
}

.total-bar {
  background: linear-gradient(135deg, #1a1a2e, #2a2a4e);
  border-radius: 12px;
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.2rem;
}

.total-bar span:first-child {
  color: #aaa;
  font-size: 0.95rem;
}

.total-value {
  font-size: 1.5rem;
  font-weight: bold;
  color: #51cf66;
}

.empty-msg {
  text-align: center;
  color: #555;
  padding: 3rem;
  font-size: 0.95rem;
}

.refresh-row {
  text-align: center;
  margin-top: 1rem;
}

.btn-refresh {
  background: #2a2a3e;
  border: 1px solid #444;
  border-radius: 8px;
  color: #aaa;
  padding: 0.5rem 1.4rem;
  font-size: 0.88rem;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-refresh:hover:not(:disabled) {
  background: #333354;
  color: #eee;
}

.btn-refresh:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
